import { createContext, useContext, useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TenantPublic } from '@/shared/contracts';
import { api } from './api';
import { bindTenant, tenantQueryKey, PUBLIC_REFRESH_MS, type TenantBoot } from './tenant';

const TenantContext = createContext<TenantBoot | null>(null);

export function TenantProvider({ tenant, children }: { tenant: TenantBoot; children: ReactNode }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: tenantQueryKey,
    queryFn: ({ signal }) => api.get('/api/v1/tenant', TenantPublic, { signal }),
    initialData: TenantPublic.parse(tenant),
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    refetchInterval: PUBLIC_REFRESH_MS,
  });
  useLayoutEffect(() => {
    return bindTenant(data);
  }, [data]);
  const previous = useRef(data);
  useEffect(() => {
    if (previous.current === data) return;
    previous.current = data;
    // These responses include academy names, issuer details or versioned badge URLs.
    void qc.invalidateQueries({
      predicate: (query) => ['me', 'credentials', 'verify'].includes(String(query.queryKey[0])),
    });
  }, [data, qc]);
  return <TenantContext.Provider value={{ ...tenant, ...data }}>{children}</TenantContext.Provider>;
}

/** Starts with the Worker snapshot, then follows confirmed changes and periodic public reads. */
export function useTenant(): TenantBoot {
  const t = useContext(TenantContext);
  if (!t) throw new Error('useTenant() outside TenantProvider');
  return t;
}
